import {
  LinearProgress,
  Avatar,
  Box,
  Button,
  CardContent,
  Divider,
  Typography,
  Grid,
  Tooltip,
  IconButton,
  DialogTitle,
  Alert,
  Dialog,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import StyledCard from '../../components/card/StyledCard';
import axiosInstance from '../../api';
import SERVER_API_URL from '../../config';
import Iconify from '../../components/iconify';
import FormProgress from './FormProgress';

const StyledDownloadLink = styled('a')({
  display: 'none',
});

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
}));

export default function DistrictAvatar({ districtToBeUpdated, setDistrictActivatedOn, notification }) {
  const [openImage, setOpenImage] = useState(false);
  const [avatar, setAvatar] = useState();
  const [progress, setProgress] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formErrorSeverity, setFormErrorSeverity] = useState('error');

  const downloadAvatar = () => {
    if (avatar) {
      const link = document.createElement('a');
      link.href = avatar;
      link.download = `${districtToBeUpdated.name}_avatar`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getAvatar = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${SERVER_API_URL}/districtAvatar/getAvatarById/${districtToBeUpdated.id}`,
        {
          responseType: 'blob',
        }
      );
      if (response.status === 200) {
        const url = URL.createObjectURL(response.data);
        setAvatar(url);
      }
    } catch (e) {
      setAvatar(null);
    }
  }, [districtToBeUpdated]);

  const deleteAvatar = async () => {
    setProgress(true);
    try {
      const response = await axiosInstance.delete(`${SERVER_API_URL}/districtAvatar/delete/${districtToBeUpdated.id}`);

      if (response.status === 200) {
        setFormError('የመገለጫ ምስል በተሳካ ሁኔታ ተሰርዟል።');
        setFormErrorSeverity('success');
        getAvatar();
      }
    } catch (e) {
      if (e.code === 'ERR_NETWORK' || !e.response) {
        notification.message = 'የአውታረ መረብ ስህተት። እባክዎ ቆየት ብለው ይሞክሩ!';
        notification.severity = 'error';
      } else {
        notification.message = 'የመገለጫ ምስልዎን መሰረዝ አልተቻለም። እባክዎ ቆየት ብለው ይሞክሩ!';
        notification.severity = 'error';
      }
    }
    setProgress(false);
  };

  const handleChange = async (e) => {
    setProgress(true);
    try {
      const formData = new FormData();
      formData.append('image', e.target.files[0]);

      const response = await axiosInstance.patch(
        `${SERVER_API_URL}/districtAvatar/update/${districtToBeUpdated && districtToBeUpdated.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
          },
        }
      );

      if (response.status === 200) {
        setFormError('የመገለጫ ምስል በተሳካ ሁኔታ ተስተካክሏል።');
        setFormErrorSeverity('success');
        getAvatar();
      }
    } catch (e) {
      if (e.response.status === 404) {
        setAvatar(null);
      }
    }
    setProgress(false);
  };

  const handleFinish = () => {
    notification.message = 'የወረዳ ምዝገባ ተሳክቷል።';
    notification.severity = 'success';
    setDistrictActivatedOn('list');
  };

  return (
    <>
      <StyledCard sx={{ padding: 10 }}>
        <FormProgress activeStep={1} />
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12}>
            {progress && <LinearProgress color="primary" />}
            <Grid item xs={12} sm={12}>
              {formError && (
                <Alert sx={{ width: '100%' }} severity={formErrorSeverity}>
                  {formError}
                </Alert>
              )}
            </Grid>
            <CardContent>
              <StyledAccount>
                <Tooltip title={`የመገለጫ እይታ`} arrow>
                  <IconButton onClick={() => setOpenImage(true)}>
                    <Avatar
                      src={`${avatar}`}
                      alt={`${districtToBeUpdated && districtToBeUpdated.firstName}'s avatar`}
                      sx={{ width: 100, height: 100 }}
                    />
                  </IconButton>
                </Tooltip>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                    {districtToBeUpdated && districtToBeUpdated.firstName}{' '}
                    {districtToBeUpdated && districtToBeUpdated.lastName}
                  </Typography>
                  <Box sx={{ display: 'flex' }}>
                    <Typography variant="h6" sx={{ color: 'darkgray' }}>
                      ስም :
                    </Typography>
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {districtToBeUpdated && districtToBeUpdated.name}
                    </Typography>
                  </Box>
                </Box>
              </StyledAccount>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2 }}>
                <Button color="warning" component="label">
                  {avatar && (
                    <>
                      <Iconify icon="icon-park:transform" sx={{ mr: 0.5 }} />
                      ለውጥ
                    </>
                  )}
                  {!avatar && (
                    <>
                      {' '}
                      <Iconify icon="bi:plus" sx={{ mr: 0.5 }} />
                      አስገባ
                    </>
                  )}
                  <input accept="image/*" type="file" hidden onChange={handleChange} />
                </Button>
                <Divider flexItem />
                <Button color="error" onClick={deleteAvatar} disabled={!avatar}>
                  <Iconify icon="bi:dash" sx={{ mr: 0.5 }} /> አስወግድ
                </Button>
                <Button color="primary" onClick={downloadAvatar} disabled={!avatar}>
                  <Iconify icon="bi:download" sx={{ mr: 0.5 }} /> አውርድ
                </Button>

                <StyledDownloadLink id="avatar-download-link" />
              </Box>
            </CardContent>
          </Grid>
          <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'right', mt: 5 }}>
            <Button variant="contained" type="submit" sx={{ minWidth: 150 }} onClick={handleFinish}>
              አጠናቅ
            </Button>
          </Grid>
        </Grid>
      </StyledCard>

      <Dialog open={openImage} onClose={() => setOpenImage(false)}>
        {avatar && <img src={avatar} alt={`${districtToBeUpdated && districtToBeUpdated.firstName} መገለጫ ፎቶ`} />}
        {!avatar && (
          <img
            src="/assets/icon/empty_district_profile.png"
            alt={`${districtToBeUpdated && districtToBeUpdated.firstName} መገለጫ ፎቶ`}
          />
        )}
        <DialogTitle>
          {districtToBeUpdated && districtToBeUpdated.firstName} {districtToBeUpdated && districtToBeUpdated.lastName}
        </DialogTitle>
      </Dialog>
    </>
  );
}

DistrictAvatar.propTypes = {
  districtToBeUpdated: PropTypes.object.isRequired,
  setDistrictActivatedOn: PropTypes.func.isRequired,
  notification: PropTypes.object.isRequired,
};
